import { nombreEnTexte } from './nombre.js';

/**
 * L'EXAMEN CLINIQUE EN LISTE, la logique sans ecran (testee par
 * `examen.test.mjs`). Brief du 23/10/2026 (Margaux, Fuga) : un examen se coche
 * point par point, trois etats, jamais pre-coche ; les chiffres restent des
 * chiffres. Decisions du fondateur le 23/10 : « tout normal » par systeme, une
 * note libre sur chaque ligne, rien n'est transforme en texte stocke.
 *
 * CE QUI EST STOCKE, et rien d'autre :
 *   - une `inspection` de type `examen` par consultation : ses `checkpoints`
 *     (un par point : cle, systeme, libelle, etat, choix, note) et un verdict ;
 *   - une `measurement` de type `constante` par chiffre (poids, temperature...),
 *     liee a l'animal et a la consultation : le graphe sur dix ans se lit la.
 * Le paragraphe lisible (`resumeDeLExamen`) se RECALCULE a l'affichage : il
 * n'est jamais ecrit, il ne peut donc pas diverger des donnees.
 */

export type EtatDuPoint = 'normal' | 'anormal' | 'nonExamine';

/** Un point du modele : ce qu'on examine, et les choix rapides quand c'est anormal. */
export interface DefinitionDuPoint {
  cle: string;
  libelle: string;
  choix: readonly string[];
  /** L'acte du Catalogue lie au point (badge « payant », plus tard). Vide en v1. */
  acteId?: string;
}

/** Une constante du modele : `metric` est la grandeur stockee dans `measurements`. */
export interface DefinitionDeLaMesure {
  metric: string;
  libelle: string;
  unite: string;
}

export interface DefinitionDuSysteme {
  cle: string;
  libelle: string;
  points: readonly DefinitionDuPoint[];
  mesures: readonly DefinitionDeLaMesure[];
}

/** Un point tel qu'il est stocke dans `inspection.checkpoints`. */
export interface PointExamine {
  cle: string;
  systeme: string;
  libelle: string;
  etat: EtatDuPoint;
  choix: string[];
  note: string;
  acteId?: string;
}

/**
 * LE MODELE PAR DEFAUT, chien et chat (decision du 23/10 : meme liste pour les
 * deux). Les systemes et les constantes sont ceux du brief ; les choix rapides
 * sont une PROPOSITION a faire valider par Margaux et deux autres veterinaires.
 * L'ecran « Modele d'examen » le remplacera par celui de la clinique.
 */
export const MODELE_PAR_DEFAUT: readonly DefinitionDuSysteme[] = [
  {
    cle: 'general',
    libelle: 'Général',
    points: [
      { cle: 'etatGeneral', libelle: 'État général', choix: ['Abattu', 'Douloureux', 'Déshydraté'] },
      { cle: 'comportement', libelle: 'Comportement', choix: ['Craintif', 'Agressif', 'Prostré'] },
      { cle: 'etatCorporel', libelle: 'État corporel', choix: ['Maigre', 'Surpoids', 'Obèse'] },
    ],
    mesures: [
      { metric: 'weight', libelle: 'Poids', unite: 'kg' },
      { metric: 'temperature', libelle: 'Température', unite: '°C' },
    ],
  },
  {
    cle: 'cardio',
    libelle: 'Cardiovasculaire',
    points: [
      { cle: 'auscultationCardiaque', libelle: 'Auscultation', choix: ['Souffle', 'Arythmie', 'Tachycardie', 'Bradycardie'] },
      { cle: 'pouls', libelle: 'Pouls', choix: ['Faible', 'Bondissant', 'Asynchrone'] },
    ],
    mesures: [{ metric: 'heartRate', libelle: 'Fréquence cardiaque', unite: '/min' }],
  },
  {
    cle: 'respiratoire',
    libelle: 'Respiratoire',
    points: [
      { cle: 'auscultationRespiratoire', libelle: 'Auscultation', choix: ['Crépitements', 'Sifflements', 'Bruits diminués'] },
      { cle: 'modeRespiratoire', libelle: 'Mode respiratoire', choix: ['Dyspnée', 'Polypnée', 'Toux'] },
    ],
    mesures: [{ metric: 'respiratoryRate', libelle: 'Fréquence respiratoire', unite: '/min' }],
  },
  {
    cle: 'muqueuses',
    libelle: 'Muqueuses',
    points: [
      { cle: 'couleurMuqueuses', libelle: 'Couleur', choix: ['Pâles', 'Congestives', 'Ictériques', 'Cyanosées'] },
      { cle: 'hydratation', libelle: 'Hydratation', choix: ['Muqueuses sèches', 'Pli de peau persistant'] },
    ],
    mesures: [{ metric: 'capillaryRefillTime', libelle: 'TRC', unite: 's' }],
  },
  {
    cle: 'ganglions',
    libelle: 'Nœuds lymphatiques',
    points: [{ cle: 'ganglions', libelle: 'Palpation', choix: ['Augmentés', 'Douloureux'] }],
    mesures: [],
  },
  {
    cle: 'yeux',
    libelle: 'Yeux',
    points: [
      { cle: 'oeilDroit', libelle: 'Droit', choix: ['Rouge', 'Écoulement', 'Opacité', 'Blépharospasme'] },
      { cle: 'oeilGauche', libelle: 'Gauche', choix: ['Rouge', 'Écoulement', 'Opacité', 'Blépharospasme'] },
    ],
    mesures: [],
  },
  {
    cle: 'oreilles',
    libelle: 'Oreilles',
    points: [
      { cle: 'oreilleDroite', libelle: 'Droite', choix: ['Conduit rouge', 'Cérumen +', 'Cérumen ++', 'Cérumen +++', 'Odeur'] },
      { cle: 'oreilleGauche', libelle: 'Gauche', choix: ['Conduit rouge', 'Cérumen +', 'Cérumen ++', 'Cérumen +++', 'Odeur'] },
    ],
    mesures: [],
  },
  {
    cle: 'bouche',
    libelle: 'Bouche et dents',
    points: [{ cle: 'bouche', libelle: 'Bouche et dents', choix: ['Tartre', 'Gingivite', 'Dent cassée', 'Halitose'] }],
    mesures: [],
  },
  {
    cle: 'peau',
    libelle: 'Peau et pelage',
    points: [{ cle: 'peau', libelle: 'Peau et pelage', choix: ['Prurit', 'Alopécie', 'Parasites', 'Masse'] }],
    mesures: [],
  },
  {
    cle: 'abdomen',
    libelle: 'Abdomen',
    points: [{ cle: 'abdomen', libelle: 'Palpation', choix: ['Douleur', 'Masse', 'Organomégalie', 'Tension'] }],
    mesures: [],
  },
  {
    cle: 'locomoteur',
    libelle: 'Locomoteur',
    points: [{ cle: 'locomoteur', libelle: 'Locomoteur', choix: ['Boiterie', 'Douleur articulaire', 'Amyotrophie'] }],
    mesures: [],
  },
  {
    cle: 'neuro',
    libelle: 'Neurologique',
    points: [{ cle: 'neuro', libelle: 'Neurologique', choix: ['Ataxie', 'Déficit proprioceptif', 'Convulsions'] }],
    mesures: [],
  },
  {
    cle: 'urogenital',
    libelle: 'Uro-génital',
    points: [{ cle: 'urogenital', libelle: 'Uro-génital', choix: ['Écoulement', 'Vessie distendue', 'Masse mammaire'] }],
    mesures: [],
  },
];

/** Les especes qui ont une liste ; les autres (NAC) s'ouvrent en texte libre (decision du 23/10). */
export const ESPECES_EN_LISTE: readonly string[] = ['Chien', 'Chat'];

const ETATS: readonly EtatDuPoint[] = ['normal', 'anormal', 'nonExamine'];

/** Relit un point stocke ; un point illisible est ignore, un etat inconnu vaut « non examine ». */
export function lirePoint(brut: unknown): PointExamine | null {
  if (typeof brut !== 'object' || brut === null) return null;
  const p = brut as Record<string, unknown>;
  if (typeof p['cle'] !== 'string' || p['cle'] === '') return null;
  const etat = ETATS.includes(p['etat'] as EtatDuPoint) ? (p['etat'] as EtatDuPoint) : 'nonExamine';
  return {
    cle: p['cle'],
    systeme: typeof p['systeme'] === 'string' ? p['systeme'] : '',
    libelle: typeof p['libelle'] === 'string' ? p['libelle'] : p['cle'],
    etat,
    choix: Array.isArray(p['choix']) ? (p['choix'] as unknown[]).filter((c): c is string => typeof c === 'string') : [],
    note: typeof p['note'] === 'string' ? p['note'] : '',
    ...(typeof p['acteId'] === 'string' && p['acteId'] !== '' ? { acteId: p['acteId'] } : {}),
  };
}

/**
 * LES POINTS DE L'EXAMEN : ceux du modele, dans son ordre, avec ce qui est deja
 * stocke. Un point stocke que le modele n'a plus (modele change depuis) est
 * garde a la fin : on ne perd jamais ce qu'un veterinaire a note.
 */
export function pointsDeLExamen(modele: readonly DefinitionDuSysteme[], stockes: readonly unknown[]): PointExamine[] {
  const lus = stockes.map(lirePoint).filter((p): p is PointExamine => p !== null);
  const parCle = new Map(lus.map((p) => [p.cle, p]));
  const duModele: PointExamine[] = modele.flatMap((systeme) =>
    systeme.points.map((d) => {
      const stocke = parCle.get(d.cle);
      return {
        cle: d.cle,
        systeme: systeme.cle,
        libelle: d.libelle,
        etat: stocke?.etat ?? 'nonExamine',
        choix: stocke?.choix ?? [],
        note: stocke?.note ?? '',
        ...(d.acteId === undefined ? {} : { acteId: d.acteId }),
      };
    }),
  );
  const connues = new Set(duModele.map((p) => p.cle));
  return [...duModele, ...lus.filter((p) => !connues.has(p.cle))];
}

/** Change un point. Repasser a « normal » ou « non examine » vide ses choix rapides, pas sa note. */
export function changerPoint(points: readonly PointExamine[], cle: string, patch: Partial<Pick<PointExamine, 'etat' | 'choix' | 'note'>>): PointExamine[] {
  return points.map((p) => {
    if (p.cle !== cle) return p;
    const suivant = { ...p, ...patch };
    return suivant.etat === 'anormal' ? suivant : { ...suivant, choix: [] };
  });
}

/**
 * « TOUT NORMAL » D'UN SYSTEME : les points NON EXAMINES du systeme passent a
 * normal. Un point deja anormal le reste : le raccourci ne defait jamais un
 * constat. C'est un geste explicite par systeme, pas un pre-cochage.
 */
export function toutNormal(points: readonly PointExamine[], systeme: string): PointExamine[] {
  return points.map((p) => (p.systeme === systeme && p.etat === 'nonExamine' ? { ...p, etat: 'normal' } : p));
}

/** Le verdict : anormal des qu'un point l'est, incomplet s'il reste un point non examine, null si rien n'est examine. */
export function verdictDe(points: readonly PointExamine[]): 'normal' | 'anormal' | 'incomplet' | null {
  if (points.every((p) => p.etat === 'nonExamine')) return null;
  if (points.some((p) => p.etat === 'anormal')) return 'anormal';
  if (points.some((p) => p.etat === 'nonExamine')) return 'incomplet';
  return 'normal';
}

/** Un nombre saisi a la francaise (« 38,6 ») ; vide ou illisible donne null. */
export function lireNombre(saisie: string): number | null {
  const propre = saisie.trim().replace(',', '.');
  if (propre === '') return null;
  const n = Number(propre);
  return Number.isFinite(n) ? n : null;
}

/**
 * LE PARAGRAPHE LISIBLE, recalcule a chaque affichage : les constantes, puis
 * chaque systeme, puis ce qui n'a PAS ete examine (l'etat qui protege le
 * veterinaire et renseigne le collegue). Une ligne par partie.
 */
export function resumeDeLExamen(
  modele: readonly DefinitionDuSysteme[],
  points: readonly PointExamine[],
  mesures: Readonly<Record<string, number>>,
): string[] {
  const lignes: string[] = [];
  const chiffres = modele
    .flatMap((s) => s.mesures)
    .filter((m) => typeof mesures[m.metric] === 'number')
    .map((m) => `${m.libelle} ${nombreEnTexte(mesures[m.metric] as number)} ${m.unite}`);
  if (chiffres.length > 0) lignes.push(`${chiffres.join(', ')}.`);
  const libelleDuSysteme = new Map(modele.map((s) => [s.cle, s.libelle]));
  const systemes = [...new Set(points.map((p) => p.systeme))];
  for (const systeme of systemes) {
    const examines = points.filter((p) => p.systeme === systeme && p.etat !== 'nonExamine');
    if (examines.length === 0) continue;
    const titre = libelleDuSysteme.get(systeme) ?? 'Autre';
    const morceaux = examines.map((p) => {
      const detail = [...(p.etat === 'anormal' ? p.choix : []), ...(p.note.trim() === '' ? [] : [p.note.trim()])];
      const nom = p.libelle === titre ? '' : `${p.libelle.toLowerCase()} `;
      return `${nom}${p.etat === 'normal' ? 'normal' : 'anormal'}${detail.length === 0 ? '' : ` (${detail.join(', ')})`}`;
    });
    lignes.push(`${titre} : ${morceaux.join(' ; ')}.`);
  }
  /* Ce qui n'a pas ete examine, groupe par systeme : « yeux (droit, gauche), bouche et dents ». */
  const oublies = points.filter((p) => p.etat === 'nonExamine');
  if (oublies.length > 0 && oublies.length < points.length) {
    const groupes = [...new Set(oublies.map((p) => p.systeme))].map((systeme) => {
      const titre = libelleDuSysteme.get(systeme) ?? '';
      const noms = oublies.filter((p) => p.systeme === systeme && p.libelle !== titre).map((p) => p.libelle.toLowerCase());
      if (titre === '') return noms.join(', ');
      return noms.length === 0 ? titre.toLowerCase() : `${titre.toLowerCase()} (${noms.join(', ')})`;
    });
    lignes.push(`Non examinés : ${groupes.join(', ')}.`);
  }
  return lignes;
}

/**
 * LE MODE AU PREMIER CLIC. Une espece sans liste (NAC) s'ouvre en texte libre.
 * Pour chien et chat : le mode que ce veterinaire a pris a son dernier examen
 * (son choix le suit d'un poste a l'autre), sinon la liste.
 */
export function modeParDefaut(
  derniers: ReadonlyArray<{ mode: unknown; quand: number }>,
  espece: unknown,
): 'liste' | 'texte' {
  if (typeof espece !== 'string' || !ESPECES_EN_LISTE.includes(espece)) return 'texte';
  const dernier = [...derniers].filter((d) => d.mode === 'liste' || d.mode === 'texte').sort((a, b) => b.quand - a.quand)[0];
  return dernier === undefined ? 'liste' : (dernier.mode as 'liste' | 'texte');
}
